import React, { useState, useRef } from "react";
import { useModalAccessibility } from "../../hooks/useModalAccessibility";

const ShareGroupModal = ({ showModal, onClose, groupData }) => {
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const modalRef = useRef(null);

  useModalAccessibility({
    isOpen: showModal,
    onClose,
    modalRef,
  });

  if (!showModal || !groupData) return null;

  const joinUrl = `${window.location.origin}/login?uCode=${groupData.code}`;

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(groupData.code);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2500);
    } catch (err) {
      console.error("Failed to copy code", err);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2500);
    } catch (err) {
      console.error("Failed to copy link", err);
    }
  };

  const handleWhatsAppShare = () => {
    const message = encodeURIComponent(
      `📸 Join our photo album "${groupData.name}" on Fotographiya!\n\n🔑 Event Code: ${groupData.code}\n🔗 Direct Link: ${joinUrl}\n\nClick the link to automatically view & find all your photos!`
    );
    window.open(`https://api.whatsapp.com/send?text=${message}`, "_blank");
  };

  return (
    <div
      className="modal-overlay"
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(15, 23, 42, 0.65)",
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 99999,
        padding: "16px",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="modal-content share-modal-content"
        ref={modalRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-album-title"
        style={{
          background: "#ffffff",
          borderRadius: "24px",
          maxWidth: "460px",
          width: "100%",
          padding: "28px 24px",
          boxShadow: "0 20px 50px rgba(0, 0, 0, 0.2)",
          position: "relative",
          outline: "none",
          animation: "modalFadeIn 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute",
            top: "18px",
            right: "18px",
            background: "#f1f5f9",
            border: "none",
            borderRadius: "50%",
            width: "36px",
            height: "36px",
            fontSize: "18px",
            fontWeight: "bold",
            color: "#64748b",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.15s ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#e2e8f0")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "#f1f5f9")}
        >
          &times;
        </button>

        {/* Modal Header */}
        <div style={{ textAlign: "center", marginBottom: "22px" }}>
          <div
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "18px",
              background: "linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "26px",
              margin: "0 auto 12px",
            }}
          >
            🔗
          </div>
          <h2
            id="share-album-title"
            style={{
              fontSize: "22px",
              fontWeight: 800,
              color: "#0f172a",
              margin: "0 0 4px",
            }}
          >
            Share Album
          </h2>
          <p style={{ fontSize: "14px", color: "#64748b", margin: 0 }}>
            Invite friends & guests to <strong>{groupData.name}</strong>
          </p>
        </div>

        {/* Section 1: Event Code */}
        <div
          style={{
            background: "#f8fafc",
            border: "1.5px solid #e2e8f0",
            borderRadius: "16px",
            padding: "16px",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              fontWeight: 700,
              color: "#64748b",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginBottom: "8px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>Event Code</span>
            <span style={{ color: "#7c3aed", fontSize: "11px", textTransform: "none", fontWeight: 600 }}>
              6-Digit Code
            </span>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "#ffffff",
              border: "1px solid #cbd5e1",
              borderRadius: "12px",
              padding: "8px 12px",
              gap: "8px",
            }}
          >
            <span
              style={{
                fontFamily: "monospace",
                fontSize: "22px",
                fontWeight: 800,
                letterSpacing: "4px",
                color: "#1e293b",
                paddingLeft: "6px",
              }}
            >
              {groupData.code}
            </span>

            <button
              type="button"
              onClick={handleCopyCode}
              style={{
                background: copiedCode ? "#16a34a" : "#7c3aed",
                color: "#ffffff",
                border: "none",
                borderRadius: "8px",
                padding: "8px 14px",
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.2s ease",
              }}
            >
              {copiedCode ? "✓ Copied!" : "📋 Copy Code"}
            </button>
          </div>
        </div>

        {/* Section 2: Direct Joining Link */}
        <div
          style={{
            background: "#f8fafc",
            border: "1.5px solid #e2e8f0",
            borderRadius: "16px",
            padding: "16px",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              fontWeight: 700,
              color: "#64748b",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginBottom: "8px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>Direct Join Link</span>
            <span style={{ color: "#16a34a", fontSize: "11px", textTransform: "none", fontWeight: 600 }}>
              ✨ Instant Access
            </span>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              background: "#ffffff",
              border: "1px solid #cbd5e1",
              borderRadius: "12px",
              padding: "4px 6px 4px 12px",
              gap: "8px",
            }}
          >
            <input
              type="text"
              readOnly
              value={joinUrl}
              style={{
                border: "none",
                outline: "none",
                background: "transparent",
                width: "100%",
                fontSize: "13px",
                color: "#334155",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
                cursor: "text",
              }}
              onClick={(e) => e.target.select()}
            />

            <button
              type="button"
              onClick={handleCopyLink}
              style={{
                background: copiedLink ? "#16a34a" : "#0f172a",
                color: "#ffffff",
                border: "none",
                borderRadius: "8px",
                padding: "8px 14px",
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
                whiteSpace: "nowrap",
                transition: "all 0.2s ease",
                flexShrink: 0,
              }}
            >
              {copiedLink ? "✓ Copied!" : "🔗 Copy Link"}
            </button>
          </div>

          <p
            style={{
              fontSize: "11.5px",
              color: "#64748b",
              marginTop: "8px",
              marginBottom: "0",
              lineHeight: 1.4,
            }}
          >
            Anyone with this link will directly join this album without needing to type the code.
          </p>
        </div>

        {/* Section 3: WhatsApp Share Button */}
        <div>
          <button
            type="button"
            onClick={handleWhatsAppShare}
            style={{
              width: "100%",
              background: "#25D366",
              color: "#ffffff",
              border: "none",
              borderRadius: "12px",
              padding: "13px 16px",
              fontSize: "15px",
              fontWeight: 700,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              boxShadow: "0 4px 12px rgba(37, 211, 102, 0.25)",
              transition: "transform 0.15s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-1px)")}
            onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
          >
            <span>💬</span> Share on WhatsApp
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareGroupModal;
